// Lib
import React, { useState, useRef, useCallback, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import Handsontable from 'handsontable/base';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import MilanoteCellEditorComponent from './modules/MilanoteCellEditorComponent';

// Components
import useTableCellEditingHandlers from './handlers/useTableCellEditingHandlers';
import TableOverlay from './handlers/TableOverlay';
import TablePopups from './TablePopups';
import HotTableRendererPortalManager from './manager/HotTableRendererPortalManager';
import useInitializeHotTable from './useInitializeHotTable';
import useTableSelectionHandlers from './handlers/useTableSelectionHandlers';
import useTableResizeEffects from './handlers/useTableResizeEffects';
import useTableMoveColumnRowHandlers from './handlers/useTableMoveColumnRowHandlers';
import useTableContextMenuHandlers from './handlers/useTableContextMenu';
import useTableSelectionEffects from './handlers/useTableSelectionEffects';
import useTableColumnResizeEffects from './handlers/useTableColumnResizeEffects';
import useTableCellEditingEffects from './handlers/useTableCellEditingEffects';
import useManualColumnResize from './handlers/useManualColumnResize';
import useTableUndoRedo from './handlers/undoRedo/useTableUndoRedo';
import { useHotStoreActiveTableSync } from './sync/useHotStoreActiveTableSync';
import { useHotToReduxSession } from './sync/useHotToReduxSession';
import { useReduxToHotRehydration } from './sync/useReduxToHotRehydration';
import { useTableEditingHandlers } from './handlers/useTableEditingHandlers';

// Utils
import { markEventAsHandled } from '../../utils/react/reactEventUtil';
import { toArray } from '../../../common/utils/immutableHelper';
import { getWidth } from '../../../common/elements/utils/elementPropertyUtils';

// Selectors
import {
    getTableContentColWidthsGUSelector,
    isCurrentlyEditingTableCell as selectIsCurrentlyEditingTableCell,
} from './tableSelector';
import { getThemeIsDarkMode } from '../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getCurrentlyEditingEditorIdForElement } from '../selection/currentlyEditingSelector';
import { userCurrencyPreferenceSelector } from '../../user/currentUserSelector';

// Types
import type { TranslationFunction } from '../../components/i18n/types';
import type { ImMNElement } from '../../../common/elements/elementModelTypes';
import type { RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import type { UndoRedoRichCellDataState } from './handlers/undoRedo/hotTableUndoRedoRichCellDataUtils';
import type { CellCoordsObj, CellSelection, CellSelections, TableContentData } from '../../../common/table/TableTypes';
import type { MilanoteHotTableCore } from './utils/hotInstanceUtils/columnSize/hotTableManualColumnLiveResizeSessionUtils';

// Styles
import './HotTable.scss';
import './HotTableRefHeaders.scss';
import './HotContextMenu.scss';

type HotTableProps = {
    element: ImMNElement;
    elementId: string;
    tableContentData: TableContentData;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    gridSize: number;
    tempSize?: RectSize | null;
    currentCellSelections?: unknown;
    currentlyEditingEditorId?: string | null;
    currentEditorKey?: string | null;
    isDragging: boolean;
    isEditable: boolean;
    isSingleSelected: boolean;
    isEditing: boolean;
    isResizing: boolean;
    inList?: string;
    isReadOnly: boolean;
    getContextZoomScale: () => number;
    showTitle: boolean;
    showCaption: boolean;
    locale: string;
    filterQuery?: string;
    translateTableContextMenu: TranslationFunction;
};

type HotTableHookProps = HotTableProps & {
    currencyPreference: string;
    currentlyEditingEditorId: string | null;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    isCurrentlyEditingTableCell: boolean;
    isDarkMode: boolean;
    tableContentColWidthsGU: number[];
    undoRedoRichCellDataState: UndoRedoRichCellDataState;
};

const HotTable: React.FC<HotTableProps> = (ownProps) => {
    const {
        element,
        elementId,
        tableContentData,
        hotTableInstanceRef,
        gridSize,
        inList,
        isReadOnly,
        isResizing,
        getContextZoomScale,
        showTitle,
        showCaption,
        locale,
        filterQuery,
        isSingleSelected,
        currentCellSelections,
        isEditing,
    } = ownProps;

    // We need a memoised version of the selector factory result, so that's why we're doing this
    const tableContentColWidthsGUSelector = useMemo(() => getTableContentColWidthsGUSelector(), []);
    const currentlyEditingEditorIdSelector = useMemo(
        () => getCurrentlyEditingEditorIdForElement(elementId),
        [elementId],
    );
    const isCurrentlyEditingTableCellSelector = useMemo(
        () => selectIsCurrentlyEditingTableCell(elementId),
        [elementId],
    );

    // @ts-ignore JS selector can't interpret the second argument
    const tableContentColWidthsGU = useSelector((state) => tableContentColWidthsGUSelector(state, { element }) || []);
    const currentlyEditingEditorId = useSelector(currentlyEditingEditorIdSelector);
    const isCurrentlyEditingTableCell = useSelector(isCurrentlyEditingTableCellSelector);
    const isDarkMode = useSelector(getThemeIsDarkMode);
    const currencyPreference = useSelector(userCurrencyPreferenceSelector);

    const [currentlyEditingCellEditorCoords, setCurrentlyEditingCellEditorCoords] = useState<CellCoordsObj | null>(
        null,
    );

    const cellEditorRef = useRef(null);
    const dropdownRef = useRef(null);

    // This stores rich Milanote cell data so that text styling etc is maintained when undoing and redoing
    const undoRedoRichCellDataStateRef = useRef<UndoRedoRichCellDataState>({});

    const hotTableContainerRef = useRef<HTMLDivElement | null>(null);
    const hotTableRendererPortalManagerRef = useRef(null);

    const elementWidth = getWidth(element);
    const isActive = isEditing || isSingleSelected;

    // Sync hot instance -> main redux store
    const activeTableSession = useHotToReduxSession({
        tableInstance: hotTableInstanceRef.current,
        elementId,
        isActive,
        isResizing,
        gridSize,
        elementWidth,
        tableContentData,
        tableContentColWidthsGU,
    });

    // Sync main redux store -> hot instance
    useReduxToHotRehydration({
        elementId,
        tableInstance: hotTableInstanceRef.current,
        isActive,
        gridSize,
        inList,
        hotTableContainerRef,
        getContextZoomScale,
        tableContentData,
        tableContentColWidthsGU,
        activeTableSession,
    });

    // Sync hot instance -> *hot* redux store
    useHotStoreActiveTableSync({
        tableInstance: hotTableInstanceRef.current,
        elementId,
        isActive,
        session: activeTableSession,
    });

    // NOTE: "props" is defined below to avoid having to change all the usages within this file after the
    //  hotTableContainerRef was moved into this component.
    const props: HotTableHookProps = {
        ...ownProps,
        currencyPreference,
        currentlyEditingEditorId,
        hotTableContainerRef,
        isCurrentlyEditingTableCell,
        isDarkMode,
        tableContentColWidthsGU,
        undoRedoRichCellDataState: undoRedoRichCellDataStateRef.current,
    };

    const { jumpToTitle, startEditingCellEditor, finishEditingCellEditor } = useTableEditingHandlers({
        hotTableInstanceRef,
        showTitle,
        elementId,
        isCurrentlyEditingTableCell,
        // TODO-TABLES determine whether this is needed any more
        setCurrentlyEditingCellEditorCoords,
    });
    const tableCellEditingHandlers = useTableCellEditingHandlers(props);
    const tableCellSelectionHandlers = useTableSelectionHandlers(props);
    const tableMoveColumnRowHandlers = useTableMoveColumnRowHandlers(props);
    // @ts-ignore - TODO - tables translation function
    const tableContextMenuHandlers = useTableContextMenuHandlers(props);

    const mounted = useInitializeHotTable({
        element,
        elementId,
        tableContentData,
        tableContentColWidthsGU,
        hotTableInstanceRef,
        hotTableContainerRef,
        hotTableRendererPortalManagerRef,
        gridSize,
        isDarkMode,
        inList,
        isReadOnly,
        isResizing,
        getContextZoomScale,
        showTitle,
        showCaption,
        locale,
        filterQuery,
        startEditingCellEditor,
        finishEditingCellEditor,
        tableCellEditingHandlers,
        tableCellSelectionHandlers,
        tableMoveColumnRowHandlers,
        tableContextMenuHandlers,
        jumpToTitle,
        cellEditorRef,
        dropdownRef,
    });

    // These need to be called after the effect that initializes the hotTableInstanceRef
    useTableUndoRedo(hotTableInstanceRef.current, undoRedoRichCellDataStateRef.current);
    useTableCellEditingEffects(props);
    useTableSelectionEffects(props);
    useTableColumnResizeEffects(props);
    useTableResizeEffects(props);
    useManualColumnResize({
        getContextZoomScale,
        gridSize,
        hotTableContainerRef,
        hotTableInstanceRef: hotTableInstanceRef as MutableRefObject<MilanoteHotTableCore | null>,
    });

    const onMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            // This is done in order to stop the parent element (in ElementContainer) to handle this event when other areas of
            // the table is clicked.
            if (isSingleSelected) markEventAsHandled(event);
        },
        [isSingleSelected],
    );

    const nRows = hotTableInstanceRef.current?.countRows() || 0;
    const nCols = hotTableInstanceRef.current?.countCols() || 0;

    const currentCellSelectionsArray = useMemo(
        () =>
            (toArray<Iterable<number>>(currentCellSelections) || []).map(
                (selection) => [...selection] as CellSelection,
            ) as CellSelections,
        [currentCellSelections],
    );

    const isSelectedLastRow = currentCellSelectionsArray.some(
        ([startRow, , endRow]) => startRow === nRows - 1 || endRow === nRows - 1,
    );

    const isSelectedLastCol = currentCellSelectionsArray.some(
        ([, startCol, , endCol]) => startCol === nCols - 1 || endCol === nCols - 1,
    );

    return (
        <div
            className={classNames('HotTable', {
                'is-selected-last-col': isSelectedLastCol,
                'is-selected-last-row': isSelectedLastRow,
            })}
            onMouseDown={onMouseDown}
        >
            <TableOverlay {...props} />

            {/* @ts-ignore - JS component */}
            <TablePopups {...props} dropdownRef={dropdownRef} cellEditorRef={cellEditorRef} />

            <MilanoteCellEditorComponent
                {...props}
                cellEditorRef={cellEditorRef}
                hotTableContainerRef={hotTableContainerRef}
                hotTableInstanceRef={hotTableInstanceRef}
                // @ts-ignore - JS component
                gridSize={gridSize}
                currentlyEditingCellEditorCoords={currentlyEditingCellEditorCoords}
                // Pass in mounted prop to ensure that the MilanoteCellEditorComponent
                // is re-rendered after HotTable is mounted
                mounted={mounted}
            />

            <HotTableRendererPortalManager ref={hotTableRendererPortalManagerRef} />

            <div className="HotTableLib" ref={hotTableContainerRef}></div>
        </div>
    );
};

export default HotTable;
